import React from 'react';
import { Linkedin, Mail, Award, Briefcase } from 'lucide-react';
import emmanuelImage from '../assets/emmanuel.jpg';

const Team: React.FC = () => {
    return (
        <section className="py-24 bg-navy-900 relative overflow-hidden">
            {/* Decorative gradient */}
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-cyan/10 rounded-full blur-[120px] opacity-20 pointer-events-none" />

            <div className="max-w-7xl mx-auto px-6 relative z-10">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                        Meet the Expert
                    </h2>
                    <p className="text-text-muted max-w-2xl mx-auto">
                        Led by a seasoned automation specialist with a proven track record of transforming business operations
                    </p>
                </div>

                <div className="max-w-4xl mx-auto">
                    <div className="grid lg:grid-cols-[300px_1fr] gap-8 items-start bg-navy-800 border border-white/[0.05] rounded-2xl p-8 md:p-12 shadow-2xl">
                        {/* Profile Image */}
                        <div className="flex flex-col items-center lg:items-start">
                            <div className="relative group">
                                {/* Blur effect positioned outside */}
                                <div className="absolute -inset-6 bg-gradient-to-br from-brand-cyan to-brand-blue rounded-3xl blur-3xl opacity-30 group-hover:opacity-50 transition-opacity pointer-events-none" />
                                {/* Image container */}
                                <div className="relative w-auto h-auto rounded-2xl overflow-hidden border-2 border-brand-cyan/30 group-hover:border-brand-cyan/50 transition-all shadow-xl">
                                    <img
                                        src={emmanuelImage}
                                        alt="Emmanuel O. Ada"
                                        className="w-full h-full object-cover"
                                        loading="eager"
                                    />
                                </div>
                            </div>

                            {/* Social Links */}
                            <div className="flex gap-3 mt-6">
                                <a
                                    href="https://www.linkedin.com/in/emmanuel-ada/"
                                    className="w-10 h-10 rounded-lg bg-white/[0.05] border border-white/[0.1] grid place-items-center text-text-muted hover:text-brand-cyan hover:border-brand-cyan transition-all"
                                    aria-label="LinkedIn"
                                >
                                    <Linkedin className="w-5 h-5" />
                                </a>
                                <a
                                    href="mailto:hello@automateit.ng"
                                    className="w-10 h-10 rounded-lg bg-white/[0.05] border border-white/[0.1] grid place-items-center text-text-muted hover:text-brand-cyan hover:border-brand-cyan transition-all"
                                    aria-label="Email"
                                >
                                    <Mail className="w-5 h-5" />
                                </a>
                            </div>
                        </div>

                        {/* Profile Content */}
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                                    Emmanuel O. Ada
                                </h3>
                                <p className="text-brand-cyan font-semibold text-lg">
                                    Lead Software Engineer & Automation Expert
                                </p>
                            </div>

                            <p className="text-text-muted leading-relaxed">
                                A software engineer with expertise in frontend, backend, mobile, and AI engineering. He specializes in building intelligent automation systems that transform complex business processes into streamlined, AI-powered workflows. His technical skills span modern web technologies, mobile development, and automation platforms like n8n, Make, and Zapier, helping businesses eliminate repetitive work and scale efficiently.
                            </p>

                            {/* Expertise Highlights */}
                            <div className="grid sm:grid-cols-2 gap-4 pt-4">
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-brand-cyan/10 grid place-items-center shrink-0">
                                        <Award className="w-5 h-5 text-brand-cyan" />
                                    </div>
                                    <div>
                                        <div className="text-white font-semibold mb-1">50+ Projects</div>
                                        <div className="text-sm text-text-muted">Successfully delivered automation solutions</div>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-brand-blue/10 grid place-items-center shrink-0">
                                        <Briefcase className="w-5 h-5 text-brand-blue" />
                                    </div>
                                    <div>
                                        <div className="text-white font-semibold mb-1">5+ Years</div>
                                        <div className="text-sm text-text-muted">Building automation systems</div>
                                    </div>
                                </div>
                            </div>

                            {/* Specializations */}
                            <div className="pt-4 border-t border-white/[0.05]">
                                <div className="text-sm font-semibold text-text-muted mb-3">CORE EXPERTISE</div>
                                <div className="flex flex-wrap gap-2">
                                    {['n8n Workflows', 'AI Integration', 'WhatsApp Automation', 'CRM Systems', 'API Development', 'Process Optimization'].map((skill) => (
                                        <span
                                            key={skill}
                                            className="px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.05] text-text-muted text-sm"
                                        >
                                            {skill}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Team;
